import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Plane, MapPin, CheckCircle2, XCircle, Clock } from "lucide-react";
import { canAccessModule } from "@/utils/permissions";
import "./cabina.css";

const fmtDate = (s) => s ? String(s).slice(0, 16).replace("T", " ") : "—";

export default function CabinaPendientes() {
  const { user } = useAuth();
  const [aeropuertos, setAeropuertos] = useState([]);
  const [destinos, setDestinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("aeropuertos");
  const isAdmin = canAccessModule(user, "admin_ventas_pendientes");

  const load = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cabina/pending-items`);
      setAeropuertos(r.data.aeropuertos || []);
      setDestinos(r.data.destinos || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar pendientes");
    }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const aprobarAero = async (iata) => {
    if (!window.confirm(`¿Aprobar el aeropuerto ${iata}? Quedará activo y visible en toda la app.`)) return;
    try {
      await axios.post(`${API}/cabina/aprobar-aeropuerto/${iata}`);
      toast.success(`Aeropuerto ${iata} aprobado`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const aprobarDest = async (id, nombre) => {
    if (!window.confirm(`¿Aprobar el destino "${nombre}"? Quedará activo y visible en toda la app.`)) return;
    try {
      await axios.post(`${API}/cabina/aprobar-destino/${id}`);
      toast.success("Destino aprobado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const rechazarAero = async (iata) => {
    if (!window.confirm(`¿Rechazar y borrar la solicitud del aeropuerto ${iata}?`)) return;
    try {
      await axios.delete(`${API}/cabina/rechazar-aeropuerto/${iata}`);
      toast.success(`Solicitud de ${iata} rechazada`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const rechazarDest = async (id) => {
    if (!window.confirm(`¿Rechazar y borrar esta solicitud de destino?`)) return;
    try {
      await axios.delete(`${API}/cabina/rechazar-destino/${id}`);
      toast.success("Solicitud rechazada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!isAdmin) {
    return (
      <div className="cv" style={{ padding: 22 }}>
        <div className="rulebox" style={{ background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }}>
          <b>Sin acceso.</b> Sólo administración puede aprobar solicitudes pendientes.
        </div>
      </div>
    );
  }

  const totalPend = aeropuertos.length + destinos.length;

  return (
    <div className="cv" style={{ padding: 22 }} data-testid="cabina-pendientes">
      <div className="head">
        <div>
          <h2>Solicitudes pendientes</h2>
          <p>Aeropuertos y destinos creados por vendedores desde Cabina que esperan tu aprobación para quedar activos en toda la app.</p>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <span className="chip c-pend" data-testid="badge-total-pending"><Clock size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{totalPend} pendiente{totalPend === 1 ? "" : "s"}</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--cv-line)", margin: "16px 0 0" }}>
        <button className="tab-btn" onClick={() => setTab("aeropuertos")} data-testid="tab-aeropuertos"
          style={{ background: "none", border: 0, padding: "10px 18px", cursor: "pointer", fontSize: 13, fontWeight: 700,
            borderBottom: tab === "aeropuertos" ? "2px solid var(--cv-blue)" : "2px solid transparent",
            color: tab === "aeropuertos" ? "var(--cv-blue)" : "var(--cv-muted)" }}>
          <Plane size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
          Aeropuertos {aeropuertos.length > 0 && <span style={{ background: "var(--cv-blue)", color: "#fff", borderRadius: 8, padding: "0 6px", marginLeft: 4, fontSize: 11 }}>{aeropuertos.length}</span>}
        </button>
        <button className="tab-btn" onClick={() => setTab("destinos")} data-testid="tab-destinos"
          style={{ background: "none", border: 0, padding: "10px 18px", cursor: "pointer", fontSize: 13, fontWeight: 700,
            borderBottom: tab === "destinos" ? "2px solid var(--cv-blue)" : "2px solid transparent",
            color: tab === "destinos" ? "var(--cv-blue)" : "var(--cv-muted)" }}>
          <MapPin size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
          Destinos {destinos.length > 0 && <span style={{ background: "var(--cv-blue)", color: "#fff", borderRadius: 8, padding: "0 6px", marginLeft: 4, fontSize: 11 }}>{destinos.length}</span>}
        </button>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="tw">
          {loading ? (
            <p style={{ padding: 20, color: "var(--cv-muted)", textAlign: "center" }}>Cargando...</p>
          ) : tab === "aeropuertos" ? (
            aeropuertos.length === 0 ? (
              <p style={{ padding: 20, color: "var(--cv-muted)", textAlign: "center" }}>Sin aeropuertos pendientes de aprobación.</p>
            ) : (
              <table>
                <thead><tr>
                  <th>IATA</th><th>Nombre</th><th>Ciudad · País</th>
                  <th>Solicitado por</th><th>Fecha</th>
                  <th className="r" style={{ width: 200 }}>Acciones</th>
                </tr></thead>
                <tbody>
                  {aeropuertos.map((a) => (
                    <tr key={a.iata_code} data-testid={`row-aero-${a.iata_code}`}>
                      <td className="mono"><b style={{ color: "var(--cv-blue)" }}>{a.iata_code}</b></td>
                      <td>{a.name}</td>
                      <td>{a.city}{a.country ? ` · ${a.country}` : ""}</td>
                      <td style={{ fontSize: 12, color: "var(--cv-muted)" }}>{a.requested_by_name || a.requested_by || "—"}</td>
                      <td className="mono" style={{ fontSize: 11.5, color: "var(--cv-muted)" }}>{fmtDate(a.requested_at)}</td>
                      <td className="r">
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button className="btn ghost sm" onClick={() => rechazarAero(a.iata_code)} style={{ color: "#991b1b", borderColor: "#fca5a5" }} data-testid={`btn-rechazar-aero-${a.iata_code}`}>
                            <XCircle size={12} />Rechazar
                          </button>
                          <button className="btn dark sm" onClick={() => aprobarAero(a.iata_code)} data-testid={`btn-aprobar-aero-${a.iata_code}`}>
                            <CheckCircle2 size={12} />Aprobar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            destinos.length === 0 ? (
              <p style={{ padding: 20, color: "var(--cv-muted)", textAlign: "center" }}>Sin destinos pendientes de aprobación.</p>
            ) : (
              <table>
                <thead><tr>
                  <th>Ciudad</th><th>Estado</th><th>País</th>
                  <th>Solicitado por</th><th>Fecha</th>
                  <th className="r" style={{ width: 200 }}>Acciones</th>
                </tr></thead>
                <tbody>
                  {destinos.map((d) => (
                    <tr key={d.id} data-testid={`row-dest-${d.id}`}>
                      <td><b>{d.city}</b></td>
                      <td>{d.state || "—"}</td>
                      <td>{d.country || "—"}</td>
                      <td style={{ fontSize: 12, color: "var(--cv-muted)" }}>{d.requested_by_name || d.requested_by || "—"}</td>
                      <td className="mono" style={{ fontSize: 11.5, color: "var(--cv-muted)" }}>{fmtDate(d.requested_at)}</td>
                      <td className="r">
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button className="btn ghost sm" onClick={() => rechazarDest(d.id)} style={{ color: "#991b1b", borderColor: "#fca5a5" }} data-testid={`btn-rechazar-dest-${d.id}`}>
                            <XCircle size={12} />Rechazar
                          </button>
                          <button className="btn dark sm" onClick={() => aprobarDest(d.id, d.city)} data-testid={`btn-aprobar-dest-${d.id}`}>
                            <CheckCircle2 size={12} />Aprobar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>
    </div>
  );
}
